/**
 * src/shell/auth/components/SecretKeyRegistrationGateway.tsx
 *
 * Sovereign Mesh Authentication Gateway.
 * Minimalist Sci-Fi Monolith interface inspired by architectural monoliths.
 * Clean, restrained, high-contrast typography with single-point telemetry datum.
 * Focuses purely on:
 *  1. Invite-gated registration with live deterministic token fingerprinting
 *  2. Out-of-Band (OOB) Email One-Time Authorization (OTA) sign-in
 *  3. Returning user 1-click mesh link
 */

import React, { useState, useEffect, useCallback } from 'react';
import { multiAccountVault, VaultAccount } from '../multi-account-vault';
import {
  Mail,
  ShieldAlert,
  ArrowRight,
  CheckCircle2,
  Lock,
  ArrowLeft,
  Ticket,
} from 'lucide-react';
import { biometricTheater } from '../services/BiometricTheater';
import { InviteTokenFingerprint } from './InviteTokenFingerprint';
import { MonolithVessel } from './MonolithVessel';

function getAuthApiBase(): string {
  if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
    return 'http://127.0.0.1:8080';
  }
  return '';
}



// Resolve local backend gateway when loaded via Electron file:// protocol
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
    return 'http://127.0.0.1:8080';
  }
  return '';
};

const getApiErrorMessage = (data: any, fallback: string): string => {
  if (data?.message && typeof data.message === 'string') return data.message;
  if (data?.error) {
    if (typeof data.error === 'string') return data.error;
    if (typeof data.error === 'object' && data.error.message) return data.error.message;
  }
  return fallback;
};

export interface SecretKeyRegistrationGatewayProps {
  onRegistered: () => void;
  isModal?: boolean;
  onCloseModal?: () => void;
}

export const SecretKeyRegistrationGateway: React.FC<SecretKeyRegistrationGatewayProps> = ({
  onRegistered,
  isModal = false,
  onCloseModal,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Email Standard Login & Invite Registration State
  const [authMode, setAuthMode] = useState<'signin' | 'register_invite'>('register_invite');
  const [inviteCodeInput, setInviteCodeInput] = useState('');
  const [recoveryStep, setRecoveryStep] = useState<'request' | 'verify'>('request');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryOtp, setRecoveryOtp] = useState('');
  const [isSigningInAsNewUser, setIsSigningInAsNewUser] = useState(false);

  // Profile & Accounts
  const [selectedAccount, setSelectedAccount] = useState<VaultAccount | null>(null);

  const triggerSuccessfulAuth = useCallback(
    (account: VaultAccount) => {
      biometricTheater.playSuccessCelebration();

      if (typeof window !== 'undefined') {
        localStorage.setItem('sovereign_onboarding_completed', 'true');
        localStorage.setItem('herx_user_role', account.role || 'member');
        if (
          account.role === 'mesh_owner' ||
          account.role === 'admin' ||
          account.role === 'superadmin' ||
          account.role === 'owner'
        ) {
          localStorage.setItem('sovereign_owner_authenticated', 'true');
          localStorage.setItem('sovereign_genesis_completed', 'true');
          localStorage.setItem('sovereign_genesis_configured', 'true');
        }
      }

      multiAccountVault.addAccount(account);
      multiAccountVault.switchAccount(account.accountId);
      onRegistered();
    },
    [onRegistered]
  );

  // ── 1. Setup & Returning User Detection ─────────────────────────────
  useEffect(() => {
    const rawAccounts = multiAccountVault.getAccounts();
    const accounts: VaultAccount[] = rawAccounts.map((a) => ({
      accountId: a.accountId,
      displayName: a.displayName,
      email: a.email,
      role: a.role || 'member',
    }));

    if (accounts.length > 0) {
      const current = multiAccountVault.getCurrentUser();
      const active: VaultAccount = current
        ? {
            accountId: current.id,
            displayName: current.displayName,
            email: current.email,
            role: current.role,
          }
        : accounts[0];

      if (active.email && !active.email.endsWith('@herx.org')) {
        // Verify active account against server enrollment gate
        fetch(`${getApiBaseUrl()}/api/v1/auth/check-enrolled`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: active.email }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data && data.ok && !data.enrolled) {
              // Accidental / un-enrolled account found in device storage: Purge it.
              multiAccountVault.removeAccount(active.accountId);
              setSelectedAccount(null);
              if (typeof window !== 'undefined') {
                localStorage.removeItem('sovereign_onboarding_completed');
                localStorage.removeItem('herx_user_role');
              }
              setErrorMessage('Account was not registered with an invite key. Please redeem a valid key.');
            } else {
              setSelectedAccount(active);
            }
          })
          .catch(() => {
            setSelectedAccount(active);
          });
      } else {
        setSelectedAccount(active);
      }
    }
  }, []);

  // ── 2. Standard Email Sign-In: Dispatch Code ────────────────────────
  const handleRequestRecoveryOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = recoveryEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setInfoMessage(null);

    try {
      const res = await fetch(`${getApiBaseUrl()}/api/v1/auth/email/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }).catch(() =>
        fetch(`${getApiBaseUrl()}/api/v1/auth/recovery/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })
      );

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403 || (data.message && data.message.includes('INVITE_REQUIRED'))) {
          setAuthMode('register_invite');
          throw new Error('This email is not yet registered. An invitation key is required.');
        }
        throw new Error(getApiErrorMessage(data, 'Failed to dispatch verification code.'));
      }

      setRecoveryStep('verify');
      if (data.devCode) {
        setRecoveryOtp(data.devCode);
      }
      setInfoMessage(data.message || `A 6-digit verification code has been dispatched to ${email}.`);
    } catch (err: any) {
      biometricTheater.playErrorFeedback();
      setErrorMessage(err.message || 'Failed to dispatch verification code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── 3. New User Registration via Invite Key + Email ─────────────────
  const handleRegisterWithInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = recoveryEmail.trim().toLowerCase();
    const inviteCode = inviteCodeInput.trim().toUpperCase();

    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    if (!inviteCode) {
      setErrorMessage('Please enter your invitation key.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setInfoMessage(null);

    try {
      const res = await fetch(`${getApiBaseUrl()}/api/v1/auth/register-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, inviteCode }),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(getApiErrorMessage(data, 'Failed to redeem invite key.'));
      }

      if (data.sessionToken) {
        const userAcc: VaultAccount = {
          accountId: data.userId || 'peer-root-owner',
          displayName: data.displayName || email.split('@')[0],
          email: data.authorizedEmail || email,
          role: data.role || 'mesh_owner',
          sessionToken: data.sessionToken,
          lastActive: Date.now(),
        };
        triggerSuccessfulAuth(userAcc);
        return;
      }

      setRecoveryStep('verify');
      if (data.devCode) {
        setRecoveryOtp(data.devCode);
      }
      setInfoMessage(data.message || `Invite key accepted! A 6-digit verification code was sent to ${email}.`);
    } catch (err: any) {
      biometricTheater.playErrorFeedback();
      setErrorMessage(err.message || 'Invite registration failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── 4. Verify OTP Code ──────────────────────────────────────────────
  const handleVerifyRecoveryOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = recoveryOtp.trim();
    const email = recoveryEmail.trim().toLowerCase();

    if (!code || code.length !== 6) {
      setErrorMessage('Please enter the 6-digit verification code.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setInfoMessage(null);

    try {
      const res = await fetch(`${getApiBaseUrl()}/api/v1/auth/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      }).catch(() =>
        fetch(`${getApiBaseUrl()}/api/v1/auth/recovery/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, code }),
        })
      );

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(getApiErrorMessage(data, 'Verification failed.'));
      }

      const userAcc: VaultAccount = {
        accountId: data.userId || `peer-usr-${Date.now()}`,
        displayName: data.displayName || email.split('@')[0],
        email: data.authorizedEmail || email,
        role: data.role || 'member',
        sessionToken: data.sessionToken,
        lastActive: Date.now(),
      };

      triggerSuccessfulAuth(userAcc);
    } catch (err: any) {
      biometricTheater.playErrorFeedback();
      setErrorMessage(err.message || 'Invalid or expired verification code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MonolithVessel
      nodeCode={
        selectedAccount && !isSigningInAsNewUser
          ? `NODE // ${selectedAccount.role.toUpperCase()}`
          : 'HERX // MESH GATE'
      }
      isModal={isModal}
      onCloseModal={onCloseModal}
    >
      {/* ── 1. RETURNING USER: 1-CLICK MESH LINK ── */}
      {selectedAccount && !isSigningInAsNewUser ? (
        <div className="monolith-fade-transition">
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '14px',
                border: '1px solid var(--mono-signal-dim)',
                overflow: 'hidden',
              }}
            >
              <img
                src={`https://api.dicebear.com/7.x/bottts/svg?seed=${selectedAccount.accountId}`}
                alt={selectedAccount.displayName}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
            <h2
              style={{
                fontSize: '18px',
                fontWeight: 600,
                margin: '0 0 6px',
                color: 'var(--mono-text-primary)',
                letterSpacing: '-0.01em',
              }}
            >
              {selectedAccount.displayName}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: 'var(--mono-text-muted)' }}>{selectedAccount.email}</span>
              <span
                style={{
                  fontSize: '9px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  backgroundColor: 'rgba(56, 189, 248, 0.1)',
                  color: 'var(--mono-signal)',
                  border: '1px solid rgba(56, 189, 248, 0.2)',
                  letterSpacing: '0.05em',
                }}
              >
                {selectedAccount.role}
              </span>
            </div>
          </div>

          {errorMessage && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: '#fca5a5',
                fontSize: '12px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <ShieldAlert size={15} color="#ef4444" style={{ flexShrink: 0 }} />
              <span>{errorMessage}</span>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              type="button"
              onClick={() => triggerSuccessfulAuth(selectedAccount)}
              className="monolith-btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <span>Establish Link</span>
              <ArrowRight size={14} />
            </button>

            <button
              type="button"
              onClick={() => {
                setIsSigningInAsNewUser(true);
                setErrorMessage(null);
                setInfoMessage(null);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--mono-text-muted)',
                fontSize: '11px',
                cursor: 'pointer',
                textAlign: 'center',
                padding: '4px',
                transition: 'color 150ms ease',
              }}
              onMouseEnter={(e) => ((e.target as HTMLElement).style.color = 'var(--mono-text-primary)')}
              onMouseLeave={(e) => ((e.target as HTMLElement).style.color = 'var(--mono-text-muted)')}
            >
              Switch identity or enter invite
            </button>
          </div>
        </div>
      ) : (
        /* ── 2. NEW USER / EMAIL AUTH / INVITE REDEMPTION ── */
        <div className="monolith-fade-transition">
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h1
              style={{
                fontSize: '19px',
                fontWeight: 600,
                margin: '0 0 6px',
                color: 'var(--mono-text-primary)',
                letterSpacing: '-0.02em',
              }}
            >
              {recoveryStep === 'verify'
                ? 'Verify Cipher'
                : authMode === 'signin'
                ? 'Peer Sign In'
                : 'Establish Identity'}
            </h1>
            <p style={{ fontSize: '12px', color: 'var(--mono-text-muted)', margin: 0, lineHeight: 1.5 }}>
              {recoveryStep === 'verify'
                ? `Enter the 6-digit verification code sent to ${recoveryEmail}`
                : authMode === 'signin'
                ? 'Authorized email access to sovereign mesh node.'
                : 'Sovereign peer mesh. Invitation key required.'}
            </p>
          </div>

          {/* Notices */}
          {errorMessage && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: '#fca5a5',
                fontSize: '12px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <ShieldAlert size={15} color="#ef4444" style={{ flexShrink: 0 }} />
              <span>{errorMessage}</span>
            </div>
          )}

          {infoMessage && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                color: '#6ee7b7',
                fontSize: '12px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <CheckCircle2 size={15} color="#10b981" style={{ flexShrink: 0 }} />
              <span>{infoMessage}</span>
            </div>
          )}

          {/* STEP A: OTP Verification Form */}
          {recoveryStep === 'verify' ? (
            <form onSubmit={handleVerifyRecoveryOtp} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <input
                  type="text"
                  required
                  autoFocus
                  maxLength={6}
                  value={recoveryOtp}
                  onChange={(e) => {
                    setRecoveryOtp(e.target.value.replace(/[^0-9]/g, ''));
                    setErrorMessage(null);
                  }}
                  placeholder="000000"
                  style={{
                    textAlign: 'center',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '20px',
                    letterSpacing: '0.3em',
                    padding: '10px',
                  }}
                />
              </div>

              <button type="submit" disabled={isSubmitting} className="monolith-btn-primary">
                {isSubmitting ? 'Authenticating...' : 'Establish Link'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setRecoveryStep('request');
                  setRecoveryOtp('');
                  setErrorMessage(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--mono-text-muted)',
                  fontSize: '11px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  marginTop: '4px',
                }}
              >
                <ArrowLeft size={12} />
                <span>Return to email dispatch</span>
              </button>
            </form>
          ) : authMode === 'signin' ? (
            /* STEP B: Email Sign In Form */
            <form onSubmit={handleRequestRecoveryOtp} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <input
                  type="email"
                  required
                  autoFocus
                  value={recoveryEmail}
                  onChange={(e) => {
                    setRecoveryEmail(e.target.value);
                    setErrorMessage(null);
                  }}
                  placeholder="name@example.com"
                />
              </div>

              <button type="submit" disabled={isSubmitting} className="monolith-btn-primary">
                {isSubmitting ? 'Dispatching...' : 'Request Code'}
              </button>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('register_invite');
                    setErrorMessage(null);
                    setInfoMessage(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--mono-signal)',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <Ticket size={12} />
                  <span>Have an Invite Key? Register Here →</span>
                </button>
              </div>
            </form>
          ) : (
            /* STEP C: Invite Key Registration Form */
            <form onSubmit={handleRegisterWithInvite} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <input
                  type="email"
                  required
                  autoFocus
                  value={recoveryEmail}
                  onChange={(e) => {
                    setRecoveryEmail(e.target.value);
                    setErrorMessage(null);
                  }}
                  placeholder="Authorized Email Address"
                />
              </div>

              <div>
                <input
                  type="text"
                  required
                  value={inviteCodeInput}
                  onChange={(e) => {
                    setInviteCodeInput(e.target.value.toUpperCase());
                    setErrorMessage(null);
                  }}
                  placeholder="INV-XXXX-XXXX"
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    letterSpacing: '0.08em',
                  }}
                  autoComplete="off"
                  spellCheck={false}
                />
                {inviteCodeInput.trim().length > 3 && (
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <InviteTokenFingerprint inviteCode={inviteCodeInput} size={28} />
                    <span style={{ fontSize: '10px', color: 'var(--mono-text-datum)', fontFamily: 'ui-monospace, monospace', letterSpacing: '0.1em' }}>
                      KEY CIPHER ACTIVE
                    </span>
                  </div>
                )}
              </div>

              <button type="submit" disabled={isSubmitting} className="monolith-btn-primary">
                {isSubmitting ? 'Validating Vouch...' : 'Establish Link'}
              </button>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('signin');
                    setErrorMessage(null);
                    setInfoMessage(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--mono-text-muted)',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => ((e.target as HTMLElement).style.color = 'var(--mono-text-primary)')}
                  onMouseLeave={(e) => ((e.target as HTMLElement).style.color = 'var(--mono-text-muted)')}
                >
                  Already Registered? Sign In →
                </button>
              </div>
            </form>
          )}

          {selectedAccount && (
            <div style={{ textAlign: 'center', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => {
                  setIsSigningInAsNewUser(false);
                  setErrorMessage(null);
                  setInfoMessage(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--mono-text-muted)',
                  fontSize: '11px',
                  cursor: 'pointer',
                }}
              >
                ← Return to {selectedAccount.displayName}
              </button>
            </div>
          )}
        </div>
      )}
    </MonolithVessel>
  );
};
